import type { MatchExpression } from "building-types/options/matchExpressionTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import { formulasEqual } from "./formulaUtils";
import { evalMatchExpression } from "./matchExpression";

export function matchExpressionFormulasEqual(a: MatchExpression<any>, b: MatchExpression<any>) {
  if (a.cases.length !== b.cases.length) {
    return false;
  }
  for (let i = 0; i < a.cases.length; i++) {
    if (!formulasEqual(a.cases[i].condition, b.cases[i].condition)) {
      return false;
    }
  }
  return true;
}

export function matchExpressionFormulasAndValuesEqual(a: MatchExpression<any>, b: MatchExpression<any>) {
  if (a.cases.length !== b.cases.length) {
    return false;
  }

  if (a.defaultValue !== b.defaultValue) {
    return false;
  }

  for (let i = 0; i < a.cases.length; i++) {
    if (!formulasEqual(a.cases[i].condition, b.cases[i].condition)) {
      return false;
    }
    if (a.cases[i].value !== b.cases[i].value) {
      return false;
    }
  }
  return true;
}

export type MatchExpressionActiveValue = MatchExpressionDefault | MatchExpressionCase;

export interface MatchExpressionDefault {
  readonly type: "DEFAULT";
}

export interface MatchExpressionCase {
  readonly type: "CASE";
  readonly caseIndex: number;
}

export function matchExpressionActiveValue(
  expression: MatchExpression<any>,
  optionChoices: OptionChoices,
): MatchExpressionActiveValue {
  const env = (variableName: string): any => {
    return optionChoices[variableName] ?? null;
  };

  const result = evalMatchExpression(expression, env);

  if (result.matchIndex !== undefined) {
    return {
      type: "CASE",
      caseIndex: result.matchIndex,
    };
  }

  return {
    type: "DEFAULT",
  };
}

export function getMatchExpressionActiveValue<T>(
  expression: MatchExpression<T>,
  activeValue: MatchExpressionActiveValue,
): T {
  if (activeValue.type === "DEFAULT") {
    return expression.defaultValue;
  }

  return expression.cases[activeValue.caseIndex].value;
}

export function setMatchExpressionActiveValue<T>(
  expression: MatchExpression<T>,
  activeValue: MatchExpressionActiveValue,
  value: T,
): MatchExpression<T> {
  if (activeValue.type === "DEFAULT") {
    return {
      ...expression,
      defaultValue: value,
    };
  }

  const newCases = [...expression.cases];
  newCases[activeValue.caseIndex] = {
    ...newCases[activeValue.caseIndex],
    value,
  };

  return {
    ...expression,
    cases: newCases,
  };
}
